import React, { useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useSelector } from "react-redux";
import { useTenantAgreement } from "services/hooks/rentals";
import { openAuthPdf } from "utilities/openAuthPdf";

const TenantAgreementActions = ({ tenantId, occupancyId }) => {
	const token = useSelector((state) => state.token);
	const fileInputRef = useRef(null);
	const [uploadOpen, setUploadOpen] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const [actionMessage, setActionMessage] = useState("");

	const {
		status,
		loading,
		error,
		isSubmitting,
		generateAgreement,
		uploadSignedAgreement,
	} = useTenantAgreement(tenantId, occupancyId);

	const viewUrl =
		status?.status === "SIGNED" && status?.signed_pdf_url
			? status.signed_pdf_url
			: status?.generated_pdf_url;

	const handleViewAgreement = async () => {
		if (!viewUrl) return;
		const result = await openAuthPdf(viewUrl, token);
		if (!result.success) {
			setActionMessage(result.message || "Could not open agreement PDF.");
		}
	};

	const handleGenerate = async () => {
		setActionMessage("");
		const result = await generateAgreement();
		if (result.success) {
			setActionMessage(result.data?.message || "Agreement generated. Download for tenant to sign.");
		} else {
			setActionMessage(result.message);
		}
	};

	const handleUpload = async () => {
		if (!selectedFile) {
			setActionMessage("Please select a signed PDF file.");
			return;
		}
		setActionMessage("");
		const result = await uploadSignedAgreement(selectedFile);
		if (result.success) {
			setUploadOpen(false);
			setSelectedFile(null);
			setActionMessage("Signed agreement uploaded successfully.");
		} else {
			setActionMessage(result.message);
		}
	};

	if (!tenantId || !occupancyId) return null;

	return (
		<Box>
			<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
				{loading && <CircularProgress size={22} />}
				{!loading && status?.status && (
					<Chip
						size="small"
						label={status.status === "SIGNED" ? "Signed on file" : "Awaiting signed upload"}
						color={status.status === "SIGNED" ? "success" : "warning"}
						variant="outlined"
					/>
				)}
				{!loading && !status?.has_agreement && (
					<Button
						variant="contained"
						size="small"
						startIcon={<DescriptionIcon />}
						onClick={handleGenerate}
						disabled={isSubmitting}
					>
						{isSubmitting ? "Generating…" : "Generate Agreement"}
					</Button>
				)}
				{!loading && status?.has_agreement && viewUrl && (
					<Button
						variant="outlined"
						size="small"
						startIcon={<DescriptionIcon />}
						onClick={handleViewAgreement}
					>
						View Agreement
					</Button>
				)}
				{!loading && status?.has_agreement && status?.status === "GENERATED" && (
					<Button
						variant="contained"
						size="small"
						startIcon={<UploadFileIcon />}
						onClick={() => {
							setActionMessage("");
							setSelectedFile(null);
							setUploadOpen(true);
						}}
						disabled={isSubmitting}
					>
						Upload Signed Agreement
					</Button>
				)}
			</Stack>

			{(error || actionMessage) && (
				<Alert
					severity={error ? "error" : "info"}
					sx={{ mt: 1, maxWidth: 360 }}
					onClose={() => setActionMessage("")}
				>
					{error || actionMessage}
				</Alert>
			)}

			<Dialog open={uploadOpen} onClose={() => !isSubmitting && setUploadOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Upload signed agreement</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Upload the tenant&apos;s signed PDF scan for this occupancy.
					</Typography>
					<Button component="label" variant="outlined" fullWidth startIcon={<UploadFileIcon />}>
						{selectedFile ? selectedFile.name : "Select signed PDF"}
						<input
							ref={fileInputRef}
							type="file"
							hidden
							accept="application/pdf,.pdf"
							onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
						/>
					</Button>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setUploadOpen(false)} disabled={isSubmitting}>
						Cancel
					</Button>
					<Button variant="contained" onClick={handleUpload} disabled={isSubmitting || !selectedFile}>
						{isSubmitting ? "Uploading…" : "Upload"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default TenantAgreementActions;
